import { createContext } from 'react';
import { decorate, observable, action, toJS, computed } from 'mobx';

import * as fn from '../utilities/_functions';

import api from '../api';

export class ExamExclusionUpdate {
    data = null;
    examTemplates = [];
    prefillExclusions = [];
    selectedExamTemplateId = null;
    selectedSectionId = null;
    isLoading = false;
    isSaving = false;
    isReady = false;
    hasUnsavedChanges = false;

    cancelExamTemplates = null;
    cancelPublicPredefinedData = null;
    cancelUserPredefinedData = null;
    cancelUserPredefinedDataUpdate = null;
    cancelRefresh = null;

    initialize = () => {
        const that = this;
        this.clear();

        return that.refresh()
            .finally(() => {
                that.isReady = true;
            })
    }

    refresh = () => {
        const that = this;

        return new Promise((resolve, reject) => {
            that.isLoading = true;
            api.ExamTemplates.search(
                {
                    parameters: [
                        {
                            field: 'PublishedTemplateId',
                            operator: '!=',
                            value: null
                        },
                        {
                            field: 'IsSystemTemplate',
                            value: false
                        },
                        {
                            field: 'IsDefault',
                            value: true
                        },
                        {
                            field: 'DeactivatedDateUtc',
                            value: null
                        }
                    ],
                    sortByFields: [{
                        field: 'CreatedDateUtc',
                        direction: 'DESC',
                    }],
                    includeTotalCount: false,
                    loadProperties: false,
                },
                (c) => { that.cancelExamTemplates = c }
            )
                .then(({ data }) => {
                    that.examTemplates = data && data.result && data.result.length > 0 ? data.result : [];
                    
                    if (!that.selectedExamTemplateId) {
                        that.selectedExamTemplateId = that.examTemplates.length > 0 ? that.examTemplates[0].id : null;
                    }

                    resolve();
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    that.isLoading = false;
                })
        })
    }

    load = (examTemplateId) => {
        const that = this;

        that.isLoading = true;

        return new Promise((resolve, reject) => {
            Promise.all([
                api.ExamPublishedTemplates.latest(examTemplateId),
                api.PretestPublishedTemplates.all(),
                api.PrefillExclusions.get(examTemplateId),
            ])
                .then((response) => {
                    if (response && response.length === 3) {
                        const template = response[0].data;
                        const data = {
                            template: template,
                            examData: [],
                        }

                        data.template.pretestTemplates = response[1].data && response[1].data.length > 0 ? response[1].data : [];

                        if (response[2].data && response[2].data.length > 0) {
                            that.prefillExclusions = response[2].data;
                        }
                        else if (data.template && data.template.prefillExclusionList && data.template.prefillExclusionList.length > 0) {
                            that.prefillExclusions = [...data.template.prefillExclusionList];
                        }
                        else {
                            that.prefillExclusions = [];
                        }

                        that.data = data;
                        that.selectedExamTemplateId = examTemplateId;
                    }

                    resolve();
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    that.isLoading = false;
                })
        })
    }

    reload = () => {
        const that = this;

        return new Promise((resolve, reject) => {
            that.load(that.selectedExamTemplateId)
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                })
        })
    }

    save = () => {
        const that = this;

        that.isSaving = true;

        return new Promise((resolve, reject) => {
            const option = {
                data: [...that.prefillExclusions],
            };

            api.PrefillExclusions.update(that.selectedExamTemplateId, option, (c) => { that.cancelUserPredefinedDataUpdate = c })
                .then(({ data }) => {
                    that.hasUnsavedChanges = false;
                    resolve(data);
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    that.isSaving = false;
                })
        })
    }

    delete = () => {
        const that = this;

        that.isSaving = true;

        return new Promise((resolve, reject) => {
            api.PrefillExclusions.delete(that.selectedExamTemplateId, (c) => { that.cancelUserPredefinedDataUpdate = c })
                .then(({ data }) => {
                    that.hasUnsavedChanges = false;
                    resolve(data);
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    that.isSaving = false;
                })
        })
    }

    clear = () => {
        this.data = null;
        this.examTemplates.clear();
        this.prefillExclusions.clear();
        this.selectedTemplateId = null;
        this.selectedSectionId = null;
        this.isLoading = false;
        this.isSaving = false;
        this.isReady = false;
        this.hasUnsavedChanges = false;

        if (fn.isFunction(this.cancelExamTemplates)) {
            this.cancelExamTemplates();
            this.cancelExamTemplates = null;
        }

        if (fn.isFunction(this.cancelPublicPredefinedData)) {
            this.cancelPublicPredefinedData();
            this.cancelPublicPredefinedData = null;
        }

        if (fn.isFunction(this.cancelUserPredefinedData)) {
            this.cancelUserPredefinedData();
            this.cancelUserPredefinedData = null;
        }

        if (fn.isFunction(this.cancelRefresh)) {
            this.cancelRefresh();
            this.cancelRefresh = null;
        }
    }

    get selectedExamTemplate() {
        if (this.selectedExamTemplateId) {
            const result = this.examTemplates.find(t => t.id === this.selectedExamTemplateId);
            return result;
        }

        return null;
    }

    get selectedSection() {
        if (!this.data || !this.data.template || !this.data.template.sections) return null;
        const sections = toJS(this.data.template.sections);
        return sections ? (sections.some(s => s.id === this.selectedSectionId) ? sections.filter(s => s.id === this.selectedSectionId)[0] : this.sortedSections[0]) : null;
    }

    get sortedSections() {
        if (!this.data || !this.data.template || !this.data.template.sections) return [];

        const sections = toJS(this.data.template.sections);
        const sortedSections = sections.sort((a, b) => { return a.displayOrder - b.displayOrder });

        return sortedSections;
    }
}

decorate(ExamExclusionUpdate, {
    data: observable,
    examTemplates: observable,
    prefillExclusions: observable,
    selectedExamTemplateId: observable,
    selectedSectionId: observable,
    isLoading: observable,
    isSaving: observable,
    isReady: observable,
    hasUnsavedChanges: observable,
    save: action,
    load: action,
    reload: action,
    delete: action,
    clear: action,
    selectedSection: computed,
    sortedSections: computed,
    selectedExamTemplate: computed,
})

export default createContext(new ExamExclusionUpdate());